import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, IconButton, Tooltip, Box, Paper, Alert, Grid, SxProps, Theme
} from '@mui/material';
import { ContentCopy, Delete } from '@mui/icons-material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';

interface APIKey {
  id: string;
  key: string;
}

const API: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' | 'info' } | null>(null);
  const auth = useContext(AuthContext);
  const TIMEOUT = 10000;

  useEffect(() => {
    const fetchApiKeys = async () => {
      if (!auth?.isAuthenticated) {
        setMessage({ text: 'Авторизуйтесь для просмотра API-ключей', type: 'error' });
        return;
      }
      setIsLoading(true);
      try {
        const response = await api.get('/api_keys', { timeout: TIMEOUT });
        setApiKeys(response.data || []);
        setMessage(null);
      } catch (error: any) {
        console.error('Ошибка загрузки ключей:', error);
        setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };
    fetchApiKeys();
  }, [auth?.isAuthenticated]);

  const createNewKey = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api_keys/new_key', { timeout: TIMEOUT });
      setApiKeys(prev => [...prev, response.data]);
      setMessage({ text: 'API-ключ успешно создан!', type: 'success' });
    } catch (error: any) {
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteApiKey = async (id: string) => {
    setIsLoading(true);
    try {
      await api.delete(`/api_keys/${id}`, { timeout: TIMEOUT });
      setApiKeys(prev => prev.filter(k => k.id !== id));
      setMessage({ text: 'Ключ удалён', type: 'success' });
    } catch (error: any) {
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const copyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setMessage({ text: 'Ключ скопирован в буфер!', type: 'info' });
    } catch {
      setMessage({ text: 'Не удалось скопировать', type: 'error' });
    }
  };

  const gridItem: SxProps<Theme> = { display: 'flex', alignItems: 'center', width: '100%' };

  if (isLoading && apiKeys.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  if (!auth?.isAuthenticated) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="text.secondary">Авторизуйтесь для работы с API-ключами</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* === ПОДРОБНОЕ ОПИСАНИЕ — КАК В НЕЙРОКОММЕНТИНГЕ === */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          API-ключи: доступ к Typhon из ваших скриптов
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          API-ключи позволяют интегрировать Typhon с вашими системами. Используйте их для автоматизации парсинга, рассылок и нейрокомментинга.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Зачем нужны API-ключи?</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              • Автоматизация задач<br />
              • Интеграция с CRM, ботами, скриптами<br />
              • Массовые операции без UI<br />
              • Безопасный доступ к данным
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Как использовать?</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              1. Создайте ключ<br />
              2. Скопируйте его (он больше не покажется!)<br />
              3. Передавайте в заголовке: <code>Authorization: Bearer YOUR_KEY</code>
            </Typography>
          </Box>

          <Box sx={{flex: 1}}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Документация</Typography>
            <Typography sx={{color: '#4B5563', fontSize: '0.9rem', mb: 1}}>
              • Доступна по адресу: <code>/docs</code><br/>
              • OpenAPI (Swagger) интерфейс<br/>
              • Примеры на Python, Node.js
            </Typography>
          </Box>
        </Box>

        <Box sx={{bgcolor: '#EBF8FF', p: 3, borderRadius: 2, borderLeft: '4px solid #1E40AF'}}>
          <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Важно:</Typography>
          <Typography sx={{color: '#4B5563', fontSize: '0.9rem'}}>
            • Ключ отображается <strong>только один раз</strong> при создании<br/>
            • Храните его в безопасном месте<br/>
            • При компрометации — немедленно удалите
          </Typography>
        </Box>

        <Typography sx={{color: '#4B5563', mt: 3, fontStyle: 'italic'}}>
          Один ключ — полный доступ к вашему аккаунту. Не передавайте его третьим лицам.
        </Typography>
      </Box>

      {message && (
          <Alert severity={message.type} sx={{mb: 3}} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
      )}

      <Paper elevation={2} sx={{borderRadius: 2, overflow: 'hidden', mb: 4}}>
        <Box sx={{
          p: 3,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h5" sx={{color: '#1E3A8A', fontWeight: 600}}>
            Мои API-ключи
          </Typography>
          <Button
              variant="contained"
              onClick={createNewKey}
              disabled={isLoading}
              sx={{bgcolor: '#1E3A8A', '&:hover': {bgcolor: '#1E40AF'}}}
          >
            Создать ключ
          </Button>
        </Box>

        <Box sx={{p: 3}}>
          {apiKeys.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{bgcolor: '#F8FAFC'}}>
                    <TableCell sx={{fontWeight: 600, color: '#1E3A8A'}}>Ключ (первые 10)</TableCell>
                    <TableCell sx={{fontWeight: 600, color: '#1E3A8A'}} align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiKeys.map(k => (
                      <TableRow key={k.id} sx={{'&:hover': {bgcolor: '#F9FAFB'}}}>
                        <TableCell>
                          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <Tooltip title={k.key} arrow placement="top">
                              <Typography sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}>
                                {k.key.substring(0, 10)}...
                              </Typography>
                            </Tooltip>
                            <IconButton
                                size="small"
                                onClick={() => copyKey(k.key)}
                                sx={{color: '#6B7280', '&:hover': {bgcolor: '#F3F4F6'}}}
                            >
                              <ContentCopy fontSize="small"/>
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                              size="small"
                              onClick={() => deleteApiKey(k.id)}
                              disabled={isLoading}
                              sx={{
                                color: '#EF4444',
                                '&:hover': {bgcolor: '#FECACA'}
                              }}
                          >
                            <Delete fontSize="small"/>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              API-ключи не созданы. Нажмите «Создать ключ», чтобы начать.
            </Typography>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default API;